// Utility functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  
  const summaryElement = openDetailsElement.querySelector('summary');
  const headerElement = event.target.closest('header[open]');
  
  if (headerElement) {
    event.preventDefault();
    headerElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    if (!facetForm) return;
    
    // Check if this form needs manual submission (has Apply button)
    const hasApplyButton = facetForm.querySelector('button[type="submit"]');
    const isMobileForm = facetForm.id === "FacetFiltersFormMobile";
    
    // ALWAYS handle sort select changes immediately (sort should work instantly)
    const sortSelect = facetForm.querySelector('select[name="sort_by"]');
    if (sortSelect) {
      sortSelect.addEventListener("change", (event) => {
        this.onSubmitHandler(event);
      });
    }
    
    if (hasApplyButton && !isMobileForm) {
      // Manual submission for sidebar and horizontal inline (filters need Apply button)
      // Submit when Apply button is clicked
      facetForm.addEventListener("submit", (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
      });
    } else if (isMobileForm) {
      // Mobile: no auto-submit, only manual via button
      facetForm.addEventListener("submit", (event) => {
        event.preventDefault();
        this.onSubmitHandler(event);
      });
    } else {
      // Auto-submit for forms without Apply button (filters auto-submit)
      facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    const productGrid = document.getElementById("ProductGridContainer");
    if (productGrid) {
      const collectionGrid = productGrid.querySelector(".collection__product-grid, .collection, #product-grid");
      if (collectionGrid) {
        collectionGrid.classList.add("loading");
      }
    }
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    // Check if this is a mobile form submission - close drawer after update
    const isMobileSubmission = event && (
      event.mobileFormSubmitted ||
      (event.target && (
        event.target.closest('#FacetFiltersFormMobile') ||
        (event.target.closest('form') && event.target.closest('form').id === 'FacetFiltersFormMobile')
      ))
    );
    const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    // Close mobile drawer after applying filters (but not for sort changes)
    if (isMobileSubmission && mobileDrawer && typeof mobileDrawer.closeDrawer === 'function') {
      const isSortChange = event && event.target && event.target.tagName === 'SELECT' && event.target.name === 'sort_by';
      if (!isSortChange) {
        // Wait for the update to complete before closing
        setTimeout(() => {
          mobileDrawer.closeDrawer();
        }, 500);
      }
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // Re-initialize mobile drawer after update
        const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');
        if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
          mobileDrawer.bindEvents();
        }
        
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        console.error('FacetFiltersForm: Error fetching section:', error);
        // Remove loading states on error
        const loadingSpinners = document.querySelectorAll(
          ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
        const productGrid = document.getElementById("ProductGridContainer");
        if (productGrid) {
          const collectionGrid = productGrid.querySelector(".collection__product-grid, .collection, #product-grid");
          if (collectionGrid) {
            collectionGrid.classList.remove("loading");
          }
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    
    // Re-initialize mobile drawer after update
    const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
      mobileDrawer.bindEvents();
    }
    
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    if (!container) {
      console.error("FacetFiltersForm: ProductGridContainer not found");
      return;
    }
    
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const newContainer = parsedHTML.getElementById("ProductGridContainer");
    if (newContainer) {
      container.innerHTML = newContainer.innerHTML;
    }

    container
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const countElement = parsedHTML.getElementById("ProductCount");
    if (!countElement) return;
    
    const count = countElement.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    
    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    
    // Check if this is a sort change - if so, update the entire form wrapper to preserve styles
    const isSortChange = event && event.target && event.target.tagName === 'SELECT' && event.target.name === 'sort_by';
    
    // Check if this is an Apply button click - need to preserve entire form structure
    // Apply button clicks trigger form submit events, so check for submit button or form submission
    const isApplyButton = event && (
      (event.target && (
        event.target.type === 'submit' || 
        event.target.closest('button[type="submit"]') ||
        (event.target.tagName === 'BUTTON' && event.target.type === 'submit')
      )) ||
      (event.type === 'submit' && !isSortChange)
    );
    
    if (isSortChange || isApplyButton) {
      // For sort changes and Apply button clicks, update the entire form wrapper to preserve all inline styles
      const formIds = ['FacetFiltersForm', 'FacetFiltersFormSidebar', 'FacetFiltersFormMobile'];
      
      formIds.forEach(formId => {
        const sourceForm = parsedHTML.getElementById(formId);
        const targetForm = document.getElementById(formId);
        
        if (sourceForm && targetForm) {
          // Store the current form element reference before replacing
          const facetFiltersForm = targetForm.closest('facet-filters-form');
          const isMobileForm = formId === 'FacetFiltersFormMobile';
          
          // Mobile forms don't have a wrapper, update form directly
          if (isMobileForm) {
            // For mobile, update the form content directly
            const sourceFormContent = sourceForm.innerHTML;
            if (sourceFormContent) {
              targetForm.innerHTML = sourceFormContent;
            }
            
            // Re-initialize custom elements
            const priceRangeElements = targetForm.querySelectorAll('price-range');
            priceRangeElements.forEach(element => {
              // Custom elements will auto-initialize via constructor
            });
            
            // Re-bind sort select listeners
            if (facetFiltersForm) {
              const sortSelect = targetForm.querySelector('select[name="sort_by"]');
              if (sortSelect) {
                const newSortSelect = sortSelect.cloneNode(true);
                sortSelect.parentNode.replaceChild(newSortSelect, sortSelect);
                newSortSelect.addEventListener("change", (e) => {
                  facetFiltersForm.onSubmitHandler(e);
                });
              }
            }
            
            // Re-bind menu drawer events after update
            const menuDrawer = targetForm.closest('menu-drawer');
            if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
              menuDrawer.bindEvents();
            }
          } else {
            // Desktop forms have wrappers
            const sourceWrapper = sourceForm.querySelector('#FacetsWrapperDesktop');
            const targetWrapper = targetForm.querySelector('#FacetsWrapperDesktop');
            
            if (sourceWrapper && targetWrapper) {
              // Replace the wrapper content - this preserves all facets and their structure
              targetWrapper.innerHTML = sourceWrapper.innerHTML;
              
              // Re-initialize custom elements that were replaced
              const priceRangeElements = targetWrapper.querySelectorAll('price-range');
              priceRangeElements.forEach(element => {
                // Custom elements will auto-initialize via constructor
              });
              
              // Re-bind sort select listeners
              if (facetFiltersForm) {
                const sortSelect = targetForm.querySelector('select[name="sort_by"]');
                if (sortSelect) {
                  const newSortSelect = sortSelect.cloneNode(true);
                  sortSelect.parentNode.replaceChild(newSortSelect, sortSelect);
                  newSortSelect.addEventListener("change", (e) => {
                    facetFiltersForm.onSubmitHandler(e);
                  });
                }
              }
            } else {
              // Fallback: if wrapper not found, try updating the form directly
              console.warn(`FacetFiltersForm: Could not find wrapper for ${formId}, attempting direct form update`);
              const sourceFormContent = sourceForm.innerHTML;
              if (sourceFormContent) {
                targetForm.innerHTML = sourceFormContent;
              }
            }
          }
        }
      });
      
      FacetFiltersForm.renderActiveFacets(parsedHTML);
      return;
    }
    
    // Original filter update logic for non-sort changes
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormSidebar .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormSidebar .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets-open-button",
      ".facet-filter__active-count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    // Update sorting components (they have inline styles that need to be preserved)
    const sortSelectors = [
      ".facets-sort-by-container",
      ".facets-horizontal-sort-container",
    ];

    sortSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      if (!sourceElement) return;
      
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        // Replace the entire element to preserve inline styles
        targetElement.outerHTML = sourceElement.outerHTML;
        
        // Re-bind the change event listener to the new select element
        const newSelect = document.querySelector(`${selector} select[name="sort_by"]`);
        if (newSelect) {
          const form = newSelect.closest('facet-filters-form');
          if (form && form.onSubmitHandler) {
            newSelect.addEventListener("change", (event) => {
              form.onSubmitHandler(event);
            });
          }
        }
      }
    });

    // Rebind events for menu drawer if it exists
    const menuDrawer = document.querySelector("menu-drawer");
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid || !productGrid.dataset.id) {
      console.error("FacetFiltersForm: product-grid element or data-id not found");
      return [];
    }
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    // Store mobile form info in event for renderPage to use
    if (event && event.target) {
      const mobileForm = event.target.closest('#FacetFiltersFormMobile');
      if (mobileForm && !event.mobileFormSubmitted) {
        event.mobileFormSubmitted = true;
      }
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    // Check if this is a sort select change
    const isSortSelect = event.target && event.target.tagName === 'SELECT' && event.target.name === 'sort_by';
    
    // Check if this is a checkbox click
    const isCheckbox = event.target && (event.target.type === 'checkbox' || event.target.classList.contains('facet-checkbox'));
    
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    const currentForm = event.target ? event.target.closest("form") : null;
    const isMobile = currentForm && currentForm.id === "FacetFiltersFormMobile";
    
    if (isCheckbox && event.target.classList.contains("facet-checkbox")) {
      // Direct checkbox click - submit just this form
      const searchParams = this.createSearchParams(currentForm);
      this.onSubmitForm(searchParams, event);
    } else if (isSortSelect) {
      // Sort select change - collect all forms (sort + filters)
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          // Desktop: include all relevant forms
          if (
            form.id === "FacetFiltersForm" ||
            form.id === "FacetFiltersFormSidebar"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          // Mobile: only mobile form
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    } else {
      // Other form submissions (Apply button, etc.)
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetFiltersForm" ||
            form.id === "FacetFiltersFormSidebar"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      // Only validate on blur (when user finishes typing) and on form submission
      element.addEventListener("blur", this.onInputBlur.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    
    // Convert dollar values to cents before form submission
    const form = this.closest("form");
    if (form) {
      form.addEventListener("submit", (e) => {
        // Validate all inputs before converting to cents
        this.querySelectorAll("input.price-range__input").forEach((input) => {
          this.adjustToValidValues(input);
        });
        this.setMinAndMaxValues();
        this.convertToCents();
      });
    }
  }

  onInputBlur(event) {
    // Validate the input value when user finishes typing
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    // Allow numbers, decimal point, comma, space, and navigation keys
    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape|Home|End/;
    if (!event.key.match(pattern)) event.preventDefault();
    
    // Prevent multiple decimal points
    if (event.key === '.' && event.currentTarget.value.includes('.')) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input.price-range__input");
    if (inputs.length < 2) return;
    
    const minInput = inputs[0];
    const maxInput = inputs[1];
    
    // Store the range max from placeholder or max attribute if available
    const rangeMax = maxInput.getAttribute("data-range-max") || 
                     (maxInput.placeholder ? parseFloat(maxInput.placeholder) : null);
    if (rangeMax) {
      minInput.setAttribute("data-range-max", rangeMax);
      maxInput.setAttribute("data-range-max", rangeMax);
    }
    
    // Set dynamic min/max constraints based on current values
    if (maxInput.value && maxInput.value.trim() !== '') {
      const maxValue = parseFloat(maxInput.value);
      if (!isNaN(maxValue)) {
        minInput.setAttribute("data-max", maxValue.toString());
      }
    }
    if (minInput.value && minInput.value.trim() !== '') {
      const minValue = parseFloat(minInput.value);
      if (!isNaN(minValue)) {
        maxInput.setAttribute("data-min", minValue.toString());
      }
    }
    if (minInput.value === "" || minInput.value.trim() === "") {
      maxInput.setAttribute("data-min", "0");
    }
    if (maxInput.value === "" || maxInput.value.trim() === "") {
      const storedMax = minInput.getAttribute("data-range-max") || minInput.getAttribute("data-max");
      if (storedMax) {
        minInput.setAttribute("data-max", storedMax);
      }
    }
  }

  adjustToValidValues(input) {
    // Only validate if there's a value
    if (!input.value || input.value.trim() === '') return;
    
    const value = parseFloat(input.value);
    if (isNaN(value)) {
      // If not a valid number, clear the input
      input.value = '';
      return;
    }
    
    const min = parseFloat(input.getAttribute("data-min") || 0);
    const maxAttr = input.getAttribute("data-max");
    const max = maxAttr ? parseFloat(maxAttr) : Infinity;
    const rangeMax = parseFloat(input.getAttribute("data-range-max") || Infinity);

    // Use the smaller of max (from data-max) or rangeMax (from data-range-max)
    // But prioritize rangeMax as it's the actual product price limit
    const actualMax = rangeMax !== Infinity ? rangeMax : max;

    if (value < min) {
      input.value = min.toString();
    } else if (value > actualMax && actualMax !== Infinity) {
      input.value = actualMax.toString();
    }
  }
  
  convertToCents() {
    // Convert dollar values to cents for Shopify
    const inputs = this.querySelectorAll("input.price-range__input");
    inputs.forEach((input) => {
      if (input.value && input.value.trim() !== '') {
        // Store original value
        input.setAttribute("data-original-value", input.value);
        // Convert to cents (multiply by 100)
        // Parse the value, allowing for decimal values
        const numericValue = parseFloat(input.value);
        if (!isNaN(numericValue)) {
          input.value = Math.round(numericValue * 100);
        }
      }
    });
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.eventsBound = false;
    this.bindEvents();
  }

  bindEvents() {
    // Prevent duplicate event binding
    if (this.eventsBound) {
      // Rebind close buttons in case they were replaced
      const closeButtons = this.querySelectorAll('.mobile-facets-close-button');
      closeButtons.forEach((closeButton) => {
        if (!closeButton.hasAttribute('data-listener-bound')) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeDrawer();
          });
          closeButton.setAttribute('data-listener-bound', 'true');
        }
      });
      return;
    }
    
    this.eventsBound = true;

    // Open drawer button
    const openButton = this.querySelector('.mobile-facets-open-button');
    if (openButton) {
      openButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }

    // Find all close buttons and bind handlers
    const closeButtons = this.querySelectorAll('.mobile-facets-close-button');
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDrawer();
      });
      closeButton.setAttribute('data-listener-bound', 'true');
    });

    // Close drawer when clicking on the overlay
    const overlay = this.querySelector('.mobile-facets-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeDrawer();
        }
      });
    }

    // Prevent clicks inside drawer from closing
    const drawer = this.querySelector('.mobile-facets-drawer');
    if (drawer) {
      drawer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Close drawer with escape key
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDrawer();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  openDrawer() {
    if (this.isOpen) return;
    
    const overlay = this.querySelector('.mobile-facets-overlay');
    const drawer = this.querySelector('.mobile-facets-drawer');
    
    if (overlay && drawer) {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      
      // Make drawer visible before animation
      drawer.style.visibility = 'visible';
      
      // Enable pointer events and fade in overlay
      overlay.classList.remove('pointer-events-none');
      overlay.classList.remove('opacity-0');
      overlay.classList.add('opacity-100');
      
      // Use requestAnimationFrame to ensure smooth animation
      requestAnimationFrame(() => {
        // Slide drawer in
        drawer.classList.remove('translate-x-full');
      });
    }
  }

  closeDrawer() {
    if (!this.isOpen) return;
    
    const overlay = this.querySelector('.mobile-facets-overlay');
    const drawer = this.querySelector('.mobile-facets-drawer');
    
    if (overlay && drawer) {
      // Start closing animation - keep visibility visible during animation
      drawer.classList.add('translate-x-full');
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0');
      
      // Wait for animation to complete before hiding and disabling
      setTimeout(() => {
        drawer.style.visibility = 'hidden';
        overlay.classList.add('pointer-events-none');
        this.isOpen = false;
        document.body.style.overflow = '';
      }, 300); // Match transition duration
    }
  }
}

customElements.define("menu-drawer", MenuDrawer);

// Handle facet dropdowns
document.addEventListener('DOMContentLoaded', function() {
  // Close other details when one opens (for horizontal facets)
  document.addEventListener('click', function(e) {
    if (e.target.closest('.js-filter details summary')) {
      const clickedDetails = e.target.closest('details');
      const allDetails = document.querySelectorAll('.js-filter details');
      
      allDetails.forEach(details => {
        if (details !== clickedDetails && details.hasAttribute('open')) {
          details.removeAttribute('open');
        }
      });
    }
  });
  
  // Close facet dropdowns when clicking outside
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.js-filter')) {
      document.querySelectorAll('.js-filter details[open]').forEach(details => {
        // Don't close mobile facets or sidebar facets, only horizontal ones
        if (!details.classList.contains('mobile-facets__details') && 
            details.closest('.js-filter.flex-shrink-0')) {
          details.removeAttribute('open');
        }
      });
    }
  });
});
